import { TextInput, Modal } from "@carbon/react";
import { useState, useEffect } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";

const TOPIC_BY_ID_QUERY = gql`
    query TopicById($id: String!) {
        topicById(id: $id) {
            id
            name
        }
    }
`;

const UPDATE_TOPIC_MUTATION = gql`
    mutation UpdateTopic($id: String!, $name: String) {
        updateTopic(id: $id, name: $name) {
            id
            name
        }
    }
`;

const DELETE_TOPIC_MUTATION = gql`
    mutation DeleteTopic($id: String!) {
        deleteTopic(id: $id) {
            id
            name
        }
    }
`;

export function TopicEdit() {
    const navigate = useNavigate();
    const { tid } = useParams(); // Get topic ID from URL
    const [updateTopic] = useMutation(UPDATE_TOPIC_MUTATION);
    const [deleteTopic, { loading: deletingTopic }] = useMutation(DELETE_TOPIC_MUTATION);
    const { data: topicData, loading, error: errorLoading } = useQuery(TOPIC_BY_ID_QUERY, {
        variables: { id: tid },
        fetchPolicy: 'cache-and-network'
    });

    const [name, setName] = useState('');
    const [continueEnabled, setContinueEnabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // Set form data once topic data is loaded
    useEffect(() => {
        if (topicData && topicData.topicById) {
            setName(topicData.topicById.name);
        }
    }, [topicData]);

    function validateForm() {
        if (name.trim() === '') {
            setErrorMessage('Themenname ist erforderlich.');
            setContinueEnabled(false);
            return false;
        }
        setErrorMessage('');
        setContinueEnabled(true);
        return true;
    }

    useEffect(() => {
        validateForm();
    }, [name]);

    async function handleSubmit(event) {
        event.preventDefault();
        if (!validateForm()) return;

        try {
            await updateTopic({
                variables: {
                    id: tid,
                    name: name.trim()
                }
            });
            navigate("/admin/topics");
        } catch (error) {
            setErrorMessage("Fehler beim Aktualisieren des Themas.");
        }
    }

    const handleDeleteTopic = async () => {
        try {
            await deleteTopic({
                variables: { id: tid }
            });
            navigate("/admin/topics");
        } catch (err) {
            setErrorMessage("Fehler beim Löschen des Themas.");
        }
    };

    if (loading) {
        return <div>Lädt Themendaten...</div>;
    }

    if (errorLoading) {
        return (
            <div className="py-4">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-800 mb-2">Thema nicht gefunden</h2>
                        <p className="text-red-700 mb-4">Das angeforderte Thema existiert nicht oder konnte nicht geladen werden.</p>
                        <button onClick={() => navigate('/admin/topics')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                            Zurück zur Themenverwaltung
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="max-w-2xl">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">
                        Thema bearbeiten
                    </h1>
                    <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="text-sm font-semibold bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded"
                    >
                        Thema löschen
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <TextInput
                        id="name"
                        labelText="Themenname"
                        placeholder="Themenname eingeben"
                        className="w-full"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                        <p className="text-sm text-red-600 h-5">
                            {errorMessage}
                        </p>
                        <button
                            type="submit"
                            className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${
                                continueEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                            }`}
                            disabled={!continueEnabled}
                        >
                            Thema aktualisieren
                        </button>
                    </div>
                </form>

                <Modal
                    open={isDeleteModalOpen}
                    danger
                    modalHeading="Thema wirklich löschen?"
                    primaryButtonText="Löschen"
                    secondaryButtonText="Abbrechen"
                    onRequestClose={() => setIsDeleteModalOpen(false)}
                    onRequestSubmit={handleDeleteTopic}
                    primaryButtonDisabled={deletingTopic}
                >
                    <p>
                        Wollen Sie das Thema <strong>{name}</strong> wirklich löschen? Dies kann nicht rückgängig gemacht werden.
                    </p>
                </Modal>
            </div>
        </div>
    );
}
